/**
 * Aides des écrans « Demandes » (ARTO-188). L'état, le numéro, le nombre de
 * modules retenus et les gestes permis sont ceux de l'API : l'écran les
 * affiche, il ne les recalcule pas. Seule l'ancienneté se déduit de la date
 * de création.
 */
import type { TFunction } from 'i18next';
import type { BadgeTone } from '@artosera/ui';
import type { EtatDemandeOuverture } from '@artosera/shared';
import { ApiError } from './api.js';
import { raisonErreur } from './modules.js';

export const CLE_DEMANDES = ['console', 'demandes-ouverture'] as const;
export const cleDemande = (id: string): readonly unknown[] => ['console', 'demande-ouverture', id];

export const TAILLE_PAGE_DEMANDES = 50;

export const TONE_ETAT_DEMANDE: Record<EtatDemandeOuverture, BadgeTone> = {
  nouvelle: 'accent',
  qualifiee: 'reserved',
  offre_envoyee: 'deposit',
  convertie: 'available',
  perdue: 'withdrawn',
};

const JOUR_MS = 86_400_000;

/**
 * Jours entiers entre le jour de création et aujourd'hui (calendrier local) :
 * 0 pour une demande reçue aujourd'hui, 1 pour hier. Les deux jours sont
 * ramenés à minuit avant la différence, donc l'heure de la demande n'y change rien.
 */
export function ancienneteEnJours(creeLe: string, maintenant: Date = new Date()): number {
  const creation = new Date(creeLe);
  if (Number.isNaN(creation.getTime())) return 0;
  const jour = (date: Date): number =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(0, Math.round((jour(maintenant) - jour(creation)) / JOUR_MS));
}

/** « Aujourd'hui », « 1 j », « 12 j ». */
export function libelleAnciennete(t: TFunction, jours: number): string {
  return jours === 0
    ? String(t('demandesOuverture.anciennete.aujourdhui'))
    : String(t('demandesOuverture.anciennete.jours', { count: jours }));
}

/** Une date et une heure lisibles dans la langue de l'écran. */
export function formaterDateHeure(valeur: string, langue: string): string {
  return new Intl.DateTimeFormat(langue, { dateStyle: 'long', timeStyle: 'short' }).format(
    new Date(valeur),
  );
}

/** Un pays à deux lettres (« FR ») en nom lisible ; le code tel quel s'il est inconnu. */
export function nomDuPays(code: string, langue: string): string {
  try {
    return new Intl.DisplayNames([langue], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Un site web saisi par un prospect ne devient un lien que s'il est en http(s). */
export function lienSiteWeb(valeur: string): string | null {
  try {
    const url = new URL(valeur.includes('://') ? valeur : `https://${valeur}`);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

export interface ErreurDemande {
  message: string;
  /** La demande lue est périmée : proposer de la recharger. */
  recharger: boolean;
  /** `demande_convertie` : le dossier vers lequel mener. */
  dossierId: string | null;
}

/** Le message d'une erreur de geste, dans la langue de l'écran. */
export function erreurDemande(t: TFunction, erreur: unknown): ErreurDemande {
  const dire = (cle: string): string => String(t(`demandesOuverture.erreurs.${cle}`));
  if (!(erreur instanceof ApiError))
    return { message: dire('reseau'), recharger: false, dossierId: null };
  const raison = raisonErreur(erreur);
  if (erreur.status === 409 && raison === 'revision_demande_perimee') {
    return { message: dire('revision_demande_perimee'), recharger: true, dossierId: null };
  }
  if (erreur.status === 409 && raison === 'demande_convertie') {
    const details = erreur.details as { dossierId?: unknown } | null;
    return {
      message: dire('demande_convertie'),
      recharger: true,
      dossierId: typeof details?.dossierId === 'string' ? details.dossierId : null,
    };
  }
  if (erreur.status === 409 && raison === 'etat_incompatible') {
    return { message: dire('etat_incompatible'), recharger: true, dossierId: null };
  }
  if (erreur.status === 403) return { message: dire('droits'), recharger: false, dossierId: null };
  return { message: dire('generique'), recharger: false, dossierId: null };
}
